import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import { AlertService } from '../../../services/general/alert.service';
import { ClinicianCandidateDocument, ClinicianCandidateDocumentStatus } from '../../../models/clinical-onboarding.model';
import { ModalAddTextComponent } from '../../../modals/modal-add-text/modal-add-text.component';

/**
 * DBEK-402: azione manager per verificare/respingere il file restituito da un
 * documento (VERIFIED/ANOMALY, generalizzato dal vecchio "firma" SIGNED - nessuna
 * cattura di firma elettronica in-app, decisione confermata). Componente riusabile,
 * pensato per essere incorporato sia nella dashboard sia nel pannello di dettaglio
 * clinico.
 */
@Component({
  selector: 'app-clinical-onboarding-document-status-action',
  templateUrl: './clinical-onboarding-document-status-action.component.html',
  styleUrls: ['./clinical-onboarding-document-status-action.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingDocumentStatusActionComponent {
  @Input() surveyId: string | number;
  @Input() document: ClinicianCandidateDocument;
  @Output() statusChanged = new EventEmitter<ClinicianCandidateDocument>();

  readonly ClinicianCandidateDocumentStatus = ClinicianCandidateDocumentStatus;
  saving = false;

  constructor(
    private clinicalOnboardingService: ClinicalOnboardingService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
    private alertService: AlertService,
  ) {}

  markAs(status: string) {
    if (status === ClinicianCandidateDocumentStatus.ANOMALY) {
      this.saving = true;
      const dialogRef = this.dialog.open(ModalAddTextComponent, {
        data: {
          title: this.translateService.instant('components.clinical-onboarding.document-action.anomaly-reason-title'),
        },
      });
      dialogRef.afterClosed().subscribe((reason) => {
        if (reason) {
          this.updateStatus(status, reason);
        } else {
          this.saving = false;
        }
      });
      return;
    }

    this.updateStatus(status, null);
  }

  /**
   * DBEK-411: a differenza di markAs(), si applica anche a un documento
   * immutabile (VERIFIED) - nessuna motivazione richiesta, solo conferma
   * (a differenza di ANOMALY).
   */
  invalidate() {
    this.alertService.showQuestion(
      this.translateService.instant('components.clinical-onboarding.document-action.invalidate-confirm-title'),
      this.translateService.instant('components.clinical-onboarding.document-action.invalidate-confirm-text'),
      () => {
        this.saving = true;
        this.clinicalOnboardingService.invalidateCandidateDocument(
          this.surveyId,
          this.document.id,
          (updated) => {
            this.saving = false;
            this.toastService.success(
              this.translateService.instant('generic.success'),
              this.translateService.instant('components.clinical-onboarding.document-action.invalidated'),
            );
            this.statusChanged.emit(updated);
          },
          () => {
            this.saving = false;
          },
        );
      },
    );
  }

  private updateStatus(status: string, anomalyReason: string) {
    this.saving = true;
    this.clinicalOnboardingService.updateCandidateDocumentStatus(
      this.surveyId,
      this.document.id,
      status,
      anomalyReason,
      (updated) => {
        this.saving = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.document-action.status-updated'),
        );
        this.statusChanged.emit(updated);
      },
      () => {
        this.saving = false;
      },
    );
  }
}
